import amplitude from 'amplitude-js';
import globalTime from '../globalTime';
import * as userAgent from '@play-co/user-agent';

import AnalyticsProperties from './AnalyticsProperties';
import GCInstantVersion from '../packageVersion';
import { GameConfig, Platform } from '../Platform';
import type ZoomTypes from '../zoom/types';
import { localStorage } from '@play-co/browser-storage';

export const AMPLITUDE_NULL_VALUE: AnalyticsProperties.AmplitudeNullValue =
  '(none)';

/**
 * Quick createGuid taken from the internet:
 * http://byronsalau.com/blog/how-to-create-a-guid-uuid-in-javascript/
 * I recommend that we switch to Chance.js or some other seeded random
 * lib in the future.
 */
function createGuid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    // eslint-disable-next-line no-mixed-operators
    const r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

abstract class Tracker {
  amplitudeClient?: amplitude.AmplitudeClient;

  constructor(public name: string, protected analytics: Analytics) {}

  abstract setUserProperties(
    newProperties: Partial<AnalyticsProperties.UserProperties>,
  ): void;

  abstract track(name: string, data?: { [key: string]: any }): void;

  trackRevenue(opts: {
    revenueType: 'in_app_purchase' | 'interstitial_ad' | 'rewarded_video';
    revenueGross: number;
    revenueGrossLocal: number;
    currencyCodeLocal: string;
    dollarToLocalRate: number;
    revenueNet: number;
    productID: string | 'interstitial_ad' | 'rewarded_video';
    feature: string;
    subFeature: string | null;

    [key: string]: any;
  }) {
    const {
      revenueType,
      revenueGross,
      revenueNet,
      productID,
      ...eventProperties
    } = opts;

    // Send a custom event to Amplitude
    this.analytics.pushEvent('Revenue', opts);

    // Send a standard event to Amplitude
    const amplitudeEvent = new amplitude.Revenue()
      .setProductId(productID)
      .setQuantity(1)
      .setPrice(revenueNet)
      .setRevenueType(revenueType)
      .setEventProperties(eventProperties);

    if (this.amplitudeClient) {
      this.amplitudeClient.logRevenueV2(amplitudeEvent);
    } else {
      console.log('logRevenueV2()', amplitudeEvent);
    }
  }

  /**
   * Returns the search query parameters that can be used for cross-domain tracking.
   */
  abstract getTrackingQueryParams(): Record<string, string>;
}

class DummyTracker extends Tracker {
  setUserProperties(
    _userProperties: Partial<AnalyticsProperties.UserProperties>,
  ) {
    // no-op
  }

  track(_name: string, _data?: { [key: string]: any }) {
    // no-op
  }

  getTrackingQueryParams() {
    return {};
  }
}

class AmplitudeTracker extends Tracker {
  _userProperties: AnalyticsProperties.UserProperties;

  constructor(opts: {
    analytics: Analytics;
    deviceID?: string;
    key: string;
    name: string;
    userID?: string;
    userProperties: AnalyticsProperties.UserProperties;
  }) {
    const { analytics, deviceID, key, name, userID, userProperties } = opts;
    super(name, analytics);

    this.amplitudeClient = amplitude.getInstance(name);

    const options: amplitude.Config = {
      // Always use HTTPS
      forceHttps: true,

      // Show less log spam
      logLevel: 'ERROR',

      // Track referrer information
      includeReferrer: true,

      // Ensure referrer is tracked every time
      saveParamsReferrerOncePerSession: false,

      // Take the device ID from the `amp_device_id` query param if available
      deviceIdFromUrlParam: true,
    };

    this.amplitudeClient.init(key, undefined, options);
    if (deviceID) {
      this.amplitudeClient.setDeviceId(deviceID);
    }
    if (userID) {
      this.amplitudeClient.setUserId(userID);
    }

    // Track releases on Amplitude
    this.amplitudeClient.setVersionName(userProperties.version);

    this._userProperties = { ...userProperties };
    this.setUserProperties(this._userProperties);
  }

  setUserProperties(
    newProperties: Partial<AnalyticsProperties.UserProperties>,
  ) {
    for (const key in newProperties) {
      this._userProperties[key] = newProperties[key];
    }

    this.amplitudeClient.setUserProperties(this._userProperties);
  }

  track(name: string, data?: { [key: string]: any }) {
    this.amplitudeClient.logEvent(name, data);
  }

  getTrackingQueryParams() {
    return { amp_device_id: this.amplitudeClient?.options.deviceId };
  }
}

class FBPixelTracker {
  _osType: any;

  constructor(osType) {
    this._osType = osType;
  }

  track(name: string, data) {
    (window as any).fbq('trackCustom', name, { ...data, osType: this._osType });
  }

  trackRevenue(data) {
    (window as any).fbq('track', 'Purchase', {
      currency: 'USD',
      value: data.price,
    });
  }
}

class PlatformTracker {
  _platform: Platform;

  constructor(platform: Platform) {
    this._platform = platform;
  }

  setUserProperties() {
    // Should have no effect
  }

  track(name: string, data) {
    // max 25 properties per event, property id can be only alphanumeric, `-` or `_`
    const properties = Object.keys(data);
    let last = properties.length;
    if (last > 25) {
      // Ensuring only first 25 alphabetically ordered properties will be processed
      // N.B We are supposing that each event always consists of the same set of properties
      properties.sort();
      last = 25;
    }

    const restrictedData: any = {};
    for (let p = 0; p < last; p++) {
      const property = properties[p];
      restrictedData[property.replace(/[^a-z0-9_]/gi, '-')] = data[property];
    }

    this._platform.logEvent(name, 1, restrictedData);
  }

  trackRevenue() {
    // Revenues are logged by default by the platform upon purchase
  }
}

export type PushRevenueOpts = {
  /**
   * Whether this came from an in-app purchase, rewarded video ad or interstitial ad.
   */
  revenueType: 'in_app_purchase' | 'interstitial_ad' | 'rewarded_video';

  /**
   * Gross revenue in USD.
   * Net revenue is:
   *  - for IAP, gross revenue minus a 30% platform cut;
   *  - for ads, the same as gross revenue.
   */
  revenueGross: number;

  /**
   * Gross revenue in the currency the revenue was generated.
   */
  revenueGrossLocal: number;
  /**
   * The currency code of the currency the revenue was generated.
   */
  currencyCodeLocal: string;
  /**
   * The USD -> local currency exchange rate used to convert
   */
  dollarToLocalRate: number;

  /**
   * The product ID where the revenue comes from.
   */
  productID: string | 'interstitial_ad' | 'rewarded_video';

  /**
   * The feature for which the purchase was triggered.
   */
  feature: string;

  /**
   * The sub-feature for which the purchase was triggered.
   * This should prefixed with the feature.
   */
  subFeature: string | null;

  /**
   * Any custom values the game wishes to track.
   */
  [key: string]: any;
};

export class Analytics {
  _commonUserProperties: any;
  _eventQueue: any[];
  _gameVersion?: string;
  private processEventQueueTimeoutHandle: null | number = null;
  _userPropertiesBacklog: any[];
  services: {
    platform?: PlatformTracker;
    default?: Tracker;
    adServer?: Tracker;
    pixel?: FBPixelTracker;
  };

  /**
   * Set analytics user properties.
   *
   * In case of user properties that are set on each entry use `Platform.sendEntryFinalAnalytics` instead of this method.
   *
   * @see https://docs.dev.gc-internal.net/gcinstant/index.html#user-properties
   *
   * @param newProperties New user properties to set. Use lowerCamelCase for naming. See Amplitude documentation for the supported user property value types.
   * @param services A list of analytics services to use. Leave undefined to use the default service, i.e., Amplitude.
   *
   * @example
   * ```ts
   * analytics.setUserProperties({ attackCount: 2, score: 10 });
   * ```
   */
  setUserProperties: (
    newProperties: Partial<AnalyticsProperties.UserProperties>,
    services?: any[],
  ) => void;

  toAdServer?: any[];
  toDefault?: any[];
  toDefaultAndAdServer?: any[];
  toDefaultAndPixel?: any[];
  toDefaultAndPixelAndAdServer?: any[];
  private _userProperties = {} as AnalyticsProperties.UserProperties;
  private config?: GameConfig;
  private zoomClientContext: ZoomTypes.ClientContext | undefined;

  constructor() {
    this.services = {};
    this._eventQueue = [];
    this._commonUserProperties = null;

    this._userPropertiesBacklog = [];

    // Temporary method that populates user property backlog, until analytics are initialized
    this.setUserProperties = (newProperties) => {
      this._userPropertiesBacklog.push(newProperties);

      for (const key in newProperties) {
        this._userProperties[key] = newProperties[key];
      }
    };
  }

  // TODO: accessing user properties through analytics is used either
  // a) internally in Platform to populate event payloads, or
  // b) in game code for game-specific analytics or hacks.
  //
  // In the future we could look into accessing userProperties through Platform,
  // i.e. making Platform the single source of truth for userProperties.

  /**
   * @returns The user properties set in the current session: this **does not include user properties set in previous sessions**.
   */
  getUserProperties(): AnalyticsProperties.UserProperties {
    return this._userProperties;
  }

  addPlatformService(platform: Platform): void {
    this.services.platform = new PlatformTracker(platform);
  }

  setZoomClientContext(clientContext: ZoomTypes.ClientContext): void {
    this.zoomClientContext = clientContext;
  }

  /**
   * Initialize analytics.
   *
   * Analytics are initialized internally in GCInstant so there's no need to call this method from game code.
   */
  initialize(
    userID: string | undefined,
    config: GameConfig & { deviceId?: string },
  ): void {
    this._gameVersion = config.version;
    this.config = config;

    this._commonUserProperties = {
      lastEntryGCInstantVersion: GCInstantVersion,
      version: this._gameVersion,
      usePolyfills: config.usePolyfills,
      lastEntryAppID: config.appID,
      playerID: userID,
      sessionID: createGuid(),
      documentReferrer: userAgent.referrer,
      userAgent: userAgent.string,
      DEVICE_TYPE: userAgent.isMobile ? 'mobile' : 'desktop',
      OS_TYPE: userAgent.os.name,
      OS_VERSION: userAgent.os.version,
      BROWSER_TYPE: userAgent.browser.name,
      BROWSER_VERSION: userAgent.browser.version,
      APP_TYPE: userAgent.app.name,
      APP_VERSION: userAgent.app.version,
    };

    if (config.amplitudeKey) {
      this.services.default = new AmplitudeTracker({
        analytics: this,
        deviceID: config.deviceId,
        key: config.amplitudeKey,
        name: 'default',
        userID,
        userProperties: this._commonUserProperties,
      });
    } else {
      this.services.default = new DummyTracker('default', this);
    }

    this.toDefault = [this.services.default];

    if (this.services.platform) {
      this.toDefault.push(this.services.platform);
    }

    if (config.amplitudeAdServerKey) {
      this.services.adServer = new AmplitudeTracker({
        analytics: this,
        deviceID: config.deviceId,
        key: config.amplitudeAdServerKey,
        name: 'adServer',
        userID,
        userProperties: {
          lastEntryVersion: this._gameVersion,
          lastEntrySourceGamePlayerID: userID,
          gameID: config.shortName,
          ...this._commonUserProperties,
        },
      });
    } else {
      this.services.adServer = new DummyTracker('adServer', this);
    }

    this.toAdServer = [this.services.adServer];
    this.toDefaultAndAdServer = this.toDefault.slice();
    this.toDefaultAndAdServer.push(this.services.adServer);

    if (config.fbPixelId) {
      this.services.pixel = new FBPixelTracker(userAgent.os.name);
      this.toDefaultAndPixel = this.toDefault.slice();
      this.toDefaultAndPixel.push(this.services.pixel);
      this.toDefaultAndPixelAndAdServer = this.toDefaultAndPixel.slice();
      this.toDefaultAndPixelAndAdServer.push(this.services.adServer);
    }

    // Processing event properties in the backlog
    if (this._userPropertiesBacklog.length > 0) {
      for (let i = 0; i < this._userPropertiesBacklog.length; i++) {
        this.services.default.setUserProperties(this._userPropertiesBacklog[i]);
      }
    }

    // Setting new user properties method
    this.setUserProperties = function setUserProperties(
      newProperties,
      services = this.toDefault || [],
    ) {
      if (config.isDevelopment) {
        console.log('analytics.setUserProperties:', newProperties);
      }

      for (let s = 0; s < services.length; s++) {
        services[s].setUserProperties(newProperties);
      }

      for (const key in newProperties) {
        this._userProperties[key] = newProperties[key];
      }
    };

    this.pushEvent('session');
    try {
      if (!localStorage.getItem('device-install')) {
        localStorage.setItem('device-install', 'true');

        if (config.fbPixelId) {
          this.pushEvent('device-install', null, this.toDefaultAndPixel);
        } else {
          this.pushEvent('device-install');
        }
      }
    } catch (error) {
      console.error('Failed to handle device-install event push', error);
    }
  }

  /**
   * Track an analytics event.
   *
   * @see https://docs.dev.gc-internal.net/gcinstant/index.html#events
   *
   * @param eventName Name of the analytics event. Use UpperCamelCase for naming.
   * @param eventProperties Event properties to attach to the event. Use lowerCamelCase for naming. See Amplitude documentation for the supported event property value types.
   * @param services A list of analytics services to use. Leave undefined to use the default service, i.e., Amplitude.
   *
   * @example
   * ```ts
   * analytics.pushEvent('LevelUp', { newLevel: 2 });
   * ```
   */
  pushEvent(
    eventName: string,
    eventProperties?: { [key: string]: any },
    services?: unknown,
  ): void {
    const curatedData: {
      sessionElapsedTime: number;
      timestamp: string;
      version: string;
    } & Partial<{ [key: string]: unknown }> = {
      sessionElapsedTime: Math.round(window.performance.now()) / 1000,
      timestamp: new Date(globalTime.getNow()).toISOString(),
      version: this._gameVersion,
    };

    for (const key in eventProperties) {
      if (eventProperties[key] !== null && eventProperties[key] !== undefined) {
        curatedData[key] = eventProperties[key];
      }
    }

    if (this.config?.isDevelopment) {
      if (curatedData.errorMessage === undefined) {
        console.log(`analytics.pushEvent: ${eventName} =`, curatedData);
      } else {
        console.log(`analytics.pushError: ${eventName} =`, curatedData);
      }
    }

    this.handleZoomUserProperties();
    this._eventQueue.push(eventName);
    this._eventQueue.push(curatedData);
    this._eventQueue.push(services);

    this.processEventQueue();
  }

  /**
   * Zoom supports two client contexts one when in meeting and one in main window.
   *
   * Set client context properties that do not belong to the current context to none.
   *
   * Reset client context properties that do belong to the current context to value from user properties.
   *
   * This is needed because amplitude uses the user properties from previous events irrespective of the context.
   * So all the client context properties need to be set again.
   */
  private handleZoomUserProperties() {
    if (!this.zoomClientContext) {
      return;
    }

    let setToNoneProps: string[] | undefined;
    let setToValueProps: string[] | undefined;
    if (this.zoomClientContext === 'inMeeting') {
      setToNoneProps = this.config?.zoomMainClientUserProperties;
      setToValueProps = this.config?.zoomMeetingClientUserProperties;
    } else if (this.zoomClientContext === 'inMainClient') {
      setToNoneProps = this.config?.zoomMeetingClientUserProperties;
      setToValueProps = this.config?.zoomMainClientUserProperties;
    }

    const userPropsToSet: Record<string, any> = {
      clientContext: this.zoomClientContext,
    };
    // set properties from different client context to none
    if (setToNoneProps) {
      for (const key of setToNoneProps) {
        userPropsToSet[key] = AMPLITUDE_NULL_VALUE;
      }
    }

    // reset properties from same client context to values from local copy
    const userProps = this.getUserProperties();
    if (setToValueProps) {
      for (const key of setToValueProps) {
        const val = userProps[key];
        if (val) {
          userPropsToSet[key] = userProps[key];
        }
      }
    }
    this.setUserProperties(userPropsToSet);
  }

  pushError(
    name: string,
    error?: Record<string, any>,
    data?: Record<string, any>,
    services?: unknown,
  ): void {
    data = data || {};
    error = error || {};

    if (this.config?.isDevelopment) {
      console.error('Sending ERROR:', name, error, data);
    }

    this.pushEvent(
      name,
      {
        ...data,
        errorCode: error.code || 'No Code',
        errorMessage: error.message || 'No Message',
        errorStack: error.stack || 'No Stack',
        viberSupportId: error.supportId,
      },
      services,
    );

    this.pushEvent(
      'Error',
      {
        ...data,
        errorOrigin: name,
        errorCode: error.code || 'No Code',
        errorMessage: error.message || 'No Message',
        errorStack: error.stack || 'No Stack',
        viberSupportId: error.supportId,
      },
      services,
    );
  }

  pushRevenue(opts: PushRevenueOpts): void {
    // Deduct applicable cuts (platforms take a 30% cut of IAP)
    const revenueNet =
      opts.revenueType === 'in_app_purchase'
        ? opts.revenueGross * 0.7
        : opts.revenueGross;

    this.services.default.trackRevenue({ ...opts, revenueNet });

    if (this.config.fbPixelId) {
      this.services.pixel.track('Revenue', {
        ...opts,
        currency: 'USD',
        value: revenueNet,
      });
    }

    // Make sure to flush immediately, to maintain as much accuracy as possible.
    this.processEventQueue();
  }

  /**
   * Process all pending events
   */
  private processEventQueue() {
    // Reschedule to later time if not yet initialized:
    if (this._commonUserProperties === null) {
      if (this.processEventQueueTimeoutHandle === null) {
        this.processEventQueueTimeoutHandle = window.setTimeout(() => {
          this.processEventQueue();
        }, 1000);
      }

      return;
    }

    for (let i = 0; i < this._eventQueue.length; i += 3) {
      const name = this._eventQueue[i];
      const data = this._eventQueue[i + 1];
      const services = this._eventQueue[i + 2] || this.toDefault;
      for (let s = 0; s < services.length; s++) {
        services[s].track(name, data);
      }
    }

    this._eventQueue.length = 0;
  }

  /**
   * Returns the search query parameters that can be used for cross-domain tracking. Will be empty when not using Amplitude.
   */
  getTrackingQueryParams(): Record<string, string> {
    return this.services.default?.getTrackingQueryParams() ?? {};
  }
}

// the analytics interface used by games to track events
export const analytics = new Analytics();
export default analytics;

// Redirecting errors

// Saving existing on error method
// to make sure we call it if another code logic uses it
const originalOnError = window.onerror;

// Keeping map of sent errors,
// to make sure errors are not sent multiple times
const sentErrors = {};

window.onerror = (msg, url, line, col, error) => {
  const errorID = line + '.' + col;
  if (sentErrors[errorID]) {
    return;
  }

  sentErrors[errorID] = true;
  analytics.pushError('window.onerror', error, {
    msg: msg,
    url: url,
    line: line,
    col: col,
  });

  originalOnError && originalOnError(msg, url, line, col, error);
};

window.addEventListener('unhandledrejection', function (rejection: any) {
  // rejection is a PromiseRejectionEvent, unless Bluebird got involved in the chain
  const error =
    rejection.reason || (rejection.detail && rejection.detail.reason);

  analytics.pushError('PromiseError', error);
});
